'use client';

import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

import { cn } from '@eliadmin/core/src/utils/style-utils';

export type PrintProblemTab = { key: number; tab: ReactNode; panel: ReactNode };

const NEXT_KEYS = ['ArrowDown', 'ArrowRight'];
const PREVIOUS_KEYS = ['ArrowUp', 'ArrowLeft'];
/** `/#problem-<id>` – the problem chips of the blog (`BlogProblems`) open that problem. */
const HASH = /^#problem-(\d+)$/;

/**
 * Problem picker as WAI-ARIA tabs with automatic activation. The list is a vertical scroll box
 * from lg (Figma 49:6439, 369 px with a 4 px scrollbar) and a horizontal scroll-snap row below, so
 * both arrow axes move the selection. Tab and panel content are rendered on the server; the inactive
 * look (Figma: whole tab at 50 %) is drawn by the tab content with `group-aria-[selected=false]/tab`,
 * so its texts keep a readable contrast. `#problem-<id>` in the URL selects that problem and scrolls
 * to the section.
 */
export function PrintProblemsTabs({
  problems,
  labelledBy,
}: {
  problems: PrintProblemTab[];
  labelledBy?: string;
}) {
  const id = useId();
  const [active, setActive] = useState(0);
  const tabs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    const openFromHash = () => {
      const key = Number(HASH.exec(window.location.hash)?.[1]);
      const index = problems.findIndex(problem => problem.key === key);
      if (index < 0) return;
      setActive(index);
      // The tab into the row on phones, then the section to the top (below the sticky header).
      tabs.current[index]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      const heading = labelledBy ? document.getElementById(labelledBy) : null;
      (heading ?? tabs.current[index])?.scrollIntoView({ block: 'start' });
    };
    openFromHash();
    window.addEventListener('hashchange', openFromHash);
    return () => window.removeEventListener('hashchange', openFromHash);
  }, [problems, labelledBy]);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const last = problems.length - 1;
    let next: number | null = null;
    if (NEXT_KEYS.includes(event.key)) next = active === last ? 0 : active + 1;
    else if (PREVIOUS_KEYS.includes(event.key)) next = active === 0 ? last : active - 1;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = last;
    if (next === null) return;

    event.preventDefault();
    setActive(next);
    tabs.current[next]?.focus();
  };

  return (
    <div className="mt-8 grid gap-4 lg:mt-10 lg:grid-cols-[314px_minmax(0,1fr)] lg:gap-6">
      <div
        role="tablist"
        aria-labelledby={labelledBy}
        onKeyDown={onKeyDown}
        className={cn(
          '-mx-4 flex snap-x snap-mandatory scroll-px-4 gap-2 overflow-x-auto px-4 py-4 sm:-mx-6 sm:scroll-px-6 sm:px-6',
          'max-lg:-my-3 max-lg:[scrollbar-width:none] max-lg:[&::-webkit-scrollbar]:hidden',
          'lg:-ml-1 lg:-mt-1 lg:mr-0 lg:h-[373px] lg:snap-none lg:flex-col lg:overflow-y-auto lg:overflow-x-hidden lg:pb-1 lg:pl-1 lg:pr-2 lg:pt-1',
          'lg:[&::-webkit-scrollbar-thumb]:rounded-full lg:[&::-webkit-scrollbar-thumb]:bg-[#E2E5DF] lg:[&::-webkit-scrollbar-track]:mt-1 lg:[&::-webkit-scrollbar-track]:rounded-full lg:[&::-webkit-scrollbar-track]:bg-[#EBEDE8] lg:[&::-webkit-scrollbar]:w-1',
        )}
      >
        {problems.map((problem, index) => (
          <button
            key={problem.key}
            ref={node => {
              tabs.current[index] = node;
            }}
            type="button"
            role="tab"
            id={`${id}-tab-${index}`}
            aria-selected={index === active}
            aria-controls={`${id}-panel-${index}`}
            tabIndex={index === active ? 0 : -1}
            onClick={() => setActive(index)}
            className="group/tab flex w-[260px] shrink-0 snap-start items-center gap-3 rounded-xl bg-white px-3 py-2 text-left shadow-soft focus-visible:shadow-focus focus-visible:outline-none lg:w-full"
          >
            {problem.tab}
          </button>
        ))}
      </div>
      {problems.map((problem, index) => (
        <div
          key={problem.key}
          role="tabpanel"
          id={`${id}-panel-${index}`}
          aria-labelledby={`${id}-tab-${index}`}
          hidden={index !== active}
          tabIndex={0}
          className="rounded-2xl bg-white p-6 shadow-raised focus-visible:shadow-focus focus-visible:outline-none lg:p-10"
        >
          {problem.panel}
        </div>
      ))}
    </div>
  );
}
